import { useMemo } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  PageShell,
  SectionCard,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import { useAdminRolesQuery } from '../hooks/useAdminRolesQuery';
import type { AdminRoleView } from '../types/admin';
import { isDisplayableRole } from '../types/admin';

export const RoleListPage = () => {
  const { t } = useTranslation('common');
  const query = useAdminRolesQuery();

  const columns = useMemo<ReadonlyArray<DataTableColumn<AdminRoleView>>>(
    () => [
      {
        key: 'code',
        header: t('admin.roles.cols.code'),
        cell: (row) => (
          <span className="font-medium" style={{ color: 'var(--content-strong)' }}>
            {t(`admin.roles.codes.${row.code}`, row.code)}
          </span>
        ),
        width: 200,
      },
      {
        key: 'rawCode',
        header: t('admin.roles.cols.rawCode'),
        cell: (row) => (
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {row.code}
          </span>
        ),
        width: 200,
      },
      {
        key: 'mandatoryTwoFactor',
        header: t('admin.roles.cols.mandatoryTwoFactor'),
        cell: (row) => (
          <StatusBadge
            label={
              row.mandatoryTwoFactor
                ? t('admin.roles.cols.mandatoryTwoFactorOn')
                : t('admin.roles.cols.mandatoryTwoFactorOff')
            }
            variant={row.mandatoryTwoFactor ? 'danger' : 'neutral'}
            size="sm"
          />
        ),
        width: 160,
      },
      {
        key: 'description',
        header: t('admin.roles.cols.description'),
        cell: (row) => (
          <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
            {t(`admin.roles.descriptions.${row.code}`, '')}
          </span>
        ),
      },
    ],
    [t],
  );

  const rows = (query.data ?? []).filter((row) => isDisplayableRole(row.code));

  return (
    <PageShell title={t('admin.roles.list.title')} data-testid="admin-role-list-page">
      <SectionCard title={t('admin.roles.list.systemDefinedTitle')} dense>
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t('admin.roles.list.systemDefinedDesc')}
        </p>
      </SectionCard>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('admin.roles.list.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('admin.common.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...rows]}
          rowKey={(row) => row.code}
          stickyHeader
          emptyState={t('admin.roles.list.empty')}
          ariaLabel={t('admin.roles.list.title')}
        />
      )}
    </PageShell>
  );
};
